import { PARTNER_ERROR_CODE } from '@steerfi/shared';
import { config } from '@/config';
import { getAuthToken } from './keycloak-auth';

const API_BASE_URL = '/internal/partners';

const buildQuery = (params: Record<string, unknown>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  const s = searchParams.toString();
  return s ? `?${s}` : '';
};

export class PartnerApiError extends Error {
  status: number;
  body?: Record<string, unknown>;

  constructor(message: string, status: number, body?: Record<string, unknown>) {
    super(message);
    this.name = 'PartnerApiError';
    this.status = status;
    if (body !== undefined) {
      this.body = body;
    }
  }
}

async function partnersRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${config.api.baseURL}${API_BASE_URL}${path}`;
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const mergedHeaders: Record<string, string> = { ...headers };
  if (
    options.headers &&
    typeof options.headers === 'object' &&
    !(options.headers instanceof Headers)
  ) {
    Object.assign(mergedHeaders, options.headers as Record<string, string>);
  }

  const response = await fetch(url, {
    ...options,
    headers: mergedHeaders,
    credentials: 'include',
    cache: 'no-store',
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    const msg =
      (typeof body.message === 'string' && body.message) ||
      (typeof body.error === 'string' && body.error) ||
      `HTTP ${response.status}`;
    throw new PartnerApiError(msg, response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }
  const parsed = JSON.parse(text) as Record<string, unknown>;
  // Unwrap { success, data } envelopes for single-record responses, but leave
  // paginated list responses intact so callers keep the pagination block.
  if (
    parsed &&
    typeof parsed === 'object' &&
    'success' in parsed &&
    'data' in parsed &&
    !('pagination' in parsed)
  ) {
    return (parsed as { data: T }).data;
  }
  return parsed as T;
}

export interface PartnerListQuery {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  order?: 'asc' | 'desc';
  search?: string;
  status?: 'active' | 'inactive';
}

export interface PartnerApiRow {
  party_id: string;
  partner_name: string;
  partner_code: string;
  metadata: Record<string, unknown> | null;
  party_role_id: number;
  status_id: number;
  created_at: string;
  updated_at: string;
  display_name: string | null;
  client_code: string | null;
}

export interface PartnerListResponse {
  data: PartnerApiRow[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface CreatePartnerDto {
  partner_name: string;
  partner_code: string;
  party_role_id: number;
  party_status_id: number;
  metadata?: Record<string, unknown> | null;
}

export interface UpdatePartnerDto {
  partner_name?: string;
  partner_code?: string;
  party_role_id?: number;
  party_status_id?: number;
  metadata?: Record<string, unknown> | null;
}

/**
 * Result of a delete attempt. The active-relationship 409 is surfaced as a
 * typed branch (rather than a thrown error) so the delete dialog can switch to
 * its blocker state without re-parsing error strings.
 */
export type DeletePartnerResult =
  | { ok: true }
  | {
      ok: false;
      code: typeof PARTNER_ERROR_CODE.HAS_ACTIVE_RELATIONSHIPS;
      activeRelationshipCount: number;
    }
  | { ok: false; code: 'OTHER'; message: string };

export const listPartners = async (
  query: PartnerListQuery = {}
): Promise<{ success: boolean; data: PartnerListResponse }> => {
  const data = await partnersRequest<PartnerListResponse>(
    `${buildQuery(query as Record<string, unknown>)}`
  );
  return { success: true, data };
};

export const getPartner = async (
  partyId: string
): Promise<{ success: boolean; data: PartnerApiRow }> => {
  const data = await partnersRequest<PartnerApiRow>(`/${partyId}`);
  return { success: true, data };
};

export const createPartner = async (
  data: CreatePartnerDto
): Promise<{ success: boolean; data: PartnerApiRow }> => {
  const row = await partnersRequest<PartnerApiRow>('', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return { success: true, data: row };
};

export const updatePartner = async (
  partyId: string,
  data: UpdatePartnerDto
): Promise<{ success: boolean; data: PartnerApiRow }> => {
  const row = await partnersRequest<PartnerApiRow>(`/${partyId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return { success: true, data: row };
};

export const deletePartner = async (partyId: string): Promise<DeletePartnerResult> => {
  try {
    await partnersRequest<void>(`/${partyId}`, { method: 'DELETE' });
    return { ok: true };
  } catch (err: unknown) {
    if (
      err instanceof PartnerApiError &&
      err.status === 409 &&
      err.body?.code === PARTNER_ERROR_CODE.HAS_ACTIVE_RELATIONSHIPS
    ) {
      return {
        ok: false,
        code: PARTNER_ERROR_CODE.HAS_ACTIVE_RELATIONSHIPS,
        activeRelationshipCount: Number(
          (err.body?.details as Record<string, unknown> | undefined)?.activeRelationshipCount ?? 0
        ),
      };
    }
    return {
      ok: false,
      code: 'OTHER',
      message: err instanceof Error ? err.message : 'Failed to delete partner',
    };
  }
};
